import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Loading, StreakBadge } from "../components/common";
import { Trophy, Target, Globe, Diamond } from "lucide-react";

const TABS = [
  { key: "overall", label: "All Games", icon: Trophy },
  { key: "leaders_501", label: "501", icon: Target },
  { key: "leaders_atw", label: "Around the World", icon: Globe },
  { key: "leaders_baseball", label: "Baseball", icon: Diamond },
];

const PERIODS = [
  { key: "all", label: "All Time" },
  { key: "season", label: "This Season" },
  { key: "month", label: "This Month" },
];

const ASC = new Set(["best_winning_leg", "avg_darts_per_target", "fastest_round_ms"]);

const SORTS = {
  overall: [
    ["matches_won", "Wins"],
    ["win_pct", "Win %"],
    ["matches_played", "Played"],
  ],
  leaders_501: [
    ["matches_won", "Wins"],
    ["win_pct", "Win %"],
    ["three_dart_avg", "3-Dart Avg"],
    ["highest_match_avg", "High Avg"],
    ["count_180", "180s"],
    ["count_140", "140+"],
    ["highest_checkout", "High CO"],
    ["best_winning_leg", "Best Leg"],
  ],
  leaders_atw: [
    ["matches_won", "Wins"],
    ["win_pct", "Win %"],
    ["hit_pct", "Hit %"],
    ["avg_darts_per_target", "Darts/Target"],
    ["fastest_round_ms", "Fastest Round"],
  ],
  leaders_baseball: [
    ["matches_won", "Wins"],
    ["win_pct", "Win %"],
    ["best_score", "Best"],
    ["avg_score", "Avg Score"],
    ["avg_runs_per_inning", "Runs/Inn"],
  ],
};

const fmtFastest = (ms) => (ms ? `${(ms / 1000).toFixed(1)}s` : "—");

export default function Leaderboards() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overall");
  const [period, setPeriod] = useState("all");
  const [sort, setSort] = useState("matches_won");
  const navigate = useNavigate();

  useEffect(() => {
    setData(null);
    api.get("/leaderboards", { params: { period } }).then((r) => setData(r.data));
  }, [period]);

  if (!data) return <Loading label="Loading leaderboards" />;

  const rows = [...(data[tab] || [])].sort((a, b) => {
    if (ASC.has(sort)) return (a[sort] || Infinity) - (b[sort] || Infinity);
    return (b[sort] ?? 0) - (a[sort] ?? 0);
  });

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-2">Standings</div>
        <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-none">
          Leaderboards
        </h1>
      </div>

      {/* Period switch */}
      <div className="flex gap-1 flex-wrap">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            data-testid={`lb-period-${p.key}`}
            onClick={() => setPeriod(p.key)}
            className={`px-4 py-2 rounded-sm font-bold uppercase text-xs tracking-wide transition-colors duration-150 ${
              period === p.key
                ? "bg-[#FFCC00] text-black"
                : "panel text-slate-400 hover:text-white"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="flex gap-1 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`lb-tab-${t.key}`}
            onClick={() => {
              setTab(t.key);
              setSort(SORTS[t.key][0][0]);
            }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-sm font-bold uppercase text-sm tracking-wide transition-colors duration-150 ${
              tab === t.key ? "bg-[#007AFF] text-white" : "panel text-slate-300 hover:border-slate-600"
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <p className="text-xs text-slate-500 -mt-1">
        A <span className="text-slate-300 font-semibold">win</span> = 1 per match — a single game or a
        full best-of series each count as one win.{" "}
        {tab === "leaders_baseball" ? "Best score" : tab === "leaders_atw" ? "Rounds W-L" : "Legs W-L"} is
        shown as detail only.
      </p>

      <div className="flex gap-1 flex-wrap items-center">
        <span className="text-xs font-bold uppercase text-slate-500 mr-1">Sort by:</span>
        {SORTS[tab].map(([key, label]) => (
          <button
            key={key}
            data-testid={`lb-sort-${key}`}
            onClick={() => setSort(key)}
            className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase transition-colors duration-150 ${
              sort === key ? "bg-[#1f1f1f] text-white border border-[#007AFF]" : "text-slate-400 hover:text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <p className="text-slate-500">No completed matches for this selection yet.</p>
      ) : (
        <div className="panel rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#262626] text-slate-500 uppercase text-[10px] tracking-wider">
                <th className="text-left p-3 w-10">#</th>
                <th className="text-left p-3">Player</th>
                <th className="text-right p-3">Wins (W-L)</th>
                <th className="text-right p-3 text-slate-500">
                  {tab === "leaders_baseball" ? "Best" : tab === "leaders_atw" ? "Rounds W-L" : "Legs W-L"}
                </th>
                <th className="text-right p-3">Win %</th>
                {tab === "leaders_baseball" && (
                  <>
                    <th className="text-right p-3">Avg</th>
                    <th className="text-right p-3">Runs/Inn</th>
                  </>
                )}
                {tab === "leaders_501" && (
                  <>
                    <th className="text-right p-3">Avg</th>
                    <th className="text-right p-3">180s</th>
                    <th className="text-right p-3">High CO</th>
                  </>
                )}
                {tab === "leaders_atw" && (
                  <>
                    <th className="text-right p-3">Hit %</th>
                    <th className="text-right p-3">D/Tgt</th>
                    <th className="text-right p-3">Fastest</th>
                  </>
                )}
                <th className="text-right p-3">Streak</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={r.id}
                  data-testid={`lb-row-${r.id}`}
                  onClick={() => navigate(`/players/${r.id}`)}
                  className="border-b border-[#1a1a1a] hover:bg-[#111] cursor-pointer"
                >
                  <td className="p-3">
                    <span
                      className={`font-display text-xl font-black ${
                        i === 0 ? "text-[#FFCC00]" : i === 1 ? "text-slate-300" : i === 2 ? "text-[#CD7F32]" : "text-slate-600"
                      }`}
                    >
                      {i + 1}
                    </span>
                  </td>
                  <td className="p-3 font-display text-lg font-bold text-white">{r.name}</td>
                  <td className="p-3 text-right font-semibold text-white">
                    {r.matches_won}-{r.matches_lost}
                  </td>
                  <td className="p-3 text-right font-semibold text-slate-500">
                    {tab === "leaders_baseball" ? r.best_score : `${r.legs_won}-${r.legs_lost}`}
                  </td>
                  <td className="p-3 text-right font-semibold text-slate-300">{r.win_pct}%</td>
                  {tab === "leaders_baseball" && (
                    <>
                      <td className="p-3 text-right font-semibold text-[#FFCC00]">{r.avg_score}</td>
                      <td className="p-3 text-right font-semibold text-[#34C759]">{r.avg_runs_per_inning}</td>
                    </>
                  )}
                  {tab === "leaders_501" && (
                    <>
                      <td className="p-3 text-right font-semibold text-[#007AFF]">{r.three_dart_avg}</td>
                      <td className="p-3 text-right font-semibold text-[#FF3B30]">{r.count_180}</td>
                      <td className="p-3 text-right font-semibold text-[#FFCC00]">{r.highest_checkout}</td>
                    </>
                  )}
                  {tab === "leaders_atw" && (
                    <>
                      <td className="p-3 text-right font-semibold text-[#34C759]">{r.hit_pct}%</td>
                      <td className="p-3 text-right font-semibold text-slate-300">{r.avg_darts_per_target}</td>
                      <td className="p-3 text-right font-semibold text-[#FFCC00]">{fmtFastest(r.fastest_round_ms)}</td>
                    </>
                  )}
                  <td className="p-3 text-right">
                    <StreakBadge streak={r.current_streak} className="text-base" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
